import { useEffect, useMemo, useState } from 'react';
import { FilterCard, MetricCard, MetricGrid, SectionCard } from '@/components/DataList';
import { ListTable, ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import { admin } from '@/api/endpoints';
import { apiBaseUrl } from '@eshhanle/shared';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { getToken } from '@/auth/session';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  Loading,
  Modal,
  ProSearch,
  Select,
  StatusBadge,
} from '@/components/ui';
import { formatDateTime, str } from '@/utils/format';

const DOC_LABELS: Record<string, string> = {
  identity: 'توثيق الهوية',
  regulatory: 'أوراق الهيئة الناظمة',
};

const IMAGE_LABELS = ['الوجه الأمامي', 'الوجه الخلفي', 'سيلفي مع الهوية'];

type PreviewFile = {
  index: number;
  label: string;
  isImage: boolean;
  ext: string;
  apiPath: string;
};

function documentFiles(doc: JsonMap): PreviewFile[] {
  const fromApi = Array.isArray(doc.files) ? (doc.files as JsonMap[]) : [];
  if (fromApi.length > 0) {
    return fromApi.map((f, i) => {
      const index = typeof f.index === 'number' ? f.index : i;
      const ext = str(f.ext, '').toLowerCase();
      const isImage = f.is_image === true || ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext);
      return {
        index,
        label: IMAGE_LABELS[index] ?? `مرفق ${index + 1}`,
        isImage,
        ext,
        apiPath: str(f.url, `/admin/compliance/file?id=${str(doc.id)}&index=${index}`),
      };
    });
  }

  const raw = str(doc.file_path, '');
  if (!raw || raw === '—') return [];
  return raw
    .split('|')
    .map((part, i) => {
      const path = part.trim();
      if (!path) return null;
      const ext = path.split('.').pop()?.toLowerCase() ?? '';
      return {
        index: i,
        label: IMAGE_LABELS[i] ?? `مرفق ${i + 1}`,
        isImage: ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext),
        ext,
        apiPath: `/admin/compliance/file?id=${str(doc.id)}&index=${i}`,
      };
    })
    .filter((item): item is PreviewFile => !!item);
}

function ComplianceAuthImage({
  apiPath,
  label,
}: {
  apiPath: string;
  label: string;
}) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl = '';
    setSrc('');
    setFailed(false);

    (async () => {
      try {
        const base = apiBaseUrl(import.meta.env.VITE_API_BASE);
        const url = `${base}${apiPath.startsWith('/') ? apiPath : `/${apiPath}`}`;
        const token = getToken();
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'image/*,application/pdf,*/*',
            'X-App-Client': 'admin',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) setFailed(true);
      }
    })();

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [apiPath]);

  if (failed) {
    return (
      <div className="compliance-image-fallback" role="img" aria-label={label}>
        تعذر تحميل الصورة
      </div>
    );
  }
  if (!src) {
    return <div className="compliance-image-fallback muted">جاري التحميل…</div>;
  }
  return <img src={src} alt={label} />;
}

export function ComplianceScreen() {
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [preview, setPreview] = useState<JsonMap | null>(null);

  const state = useAsync(
    () => admin.compliance(),
    (p) => pickList(p, 'data', 'documents', 'items'),
  );

  const list = state.data ?? [];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return list.filter((d) => {
      if (statusFilter !== 'all' && str(d.status) !== statusFilter) return false;
      if (!needle) return true;
      const hay = [str(d.user_name), str(d.user_phone), str(d.full_name), str(d.doc_type), str(d.national_id)]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [list, q, statusFilter]);

  const pendingCount = filtered.filter((d) => str(d.status) === 'pending').length;
  const previewFiles = preview ? documentFiles(preview) : [];
  const previewAddress = preview
    ? str(preview.address, str(preview.user_address, '—'))
    : '—';

  async function review(id: unknown, action: 'approve' | 'reject') {
    const note = window.prompt(action === 'approve' ? 'ملاحظة الموافقة (اختياري)' : 'سبب الرفض', '');
    if (note === null) return;
    const status = action === 'approve' ? 'approved' : 'rejected';
    const res = await admin.reviewCompliance({
      id,
      status,
      action: status,
      admin_note: note,
    });
    if (!res.success) {
      window.alert(errorMessage(res));
      return;
    }
    setPreview(null);
    state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="shield"
      title="مراجعة الوثائق"
      subtitle="راجع طلبات توثيق الهوية والامتثال التنظيمي للزبائن مع معاينة الصور."
      actions={
        <Button type="button" variant="secondary" onClick={() => state.reload()}>
          <Icon name="refresh" size={18} /> تحديث
        </Button>
      }
    >
      <MetricGrid>
        <MetricCard title="الطلبات" value={String(filtered.length)} icon={<Icon name="shield" size={21} />} />
        <MetricCard title="معلّقة" value={String(pendingCount)} icon={<Icon name="bell" size={21} />} />
      </MetricGrid>

      <FilterCard title="تصفية الوثائق" icon={<Icon name="settings" size={24} />}>
        <ProSearch value={q} onChange={setQ} placeholder="بحث بالاسم، الهاتف، نوع الوثيقة" />
        <Select
          label="الحالة"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'all', label: 'الكل' },
            { value: 'pending', label: 'معلّق' },
            { value: 'approved', label: 'موافق عليه' },
            { value: 'rejected', label: 'مرفوض' },
          ]}
        />
      </FilterCard>

      <SectionCard title={`وثائق الامتثال (${filtered.length})`} icon={<Icon name="shield" size={24} />}>
        {filtered.length === 0 ? (
          <Empty title="لا وثائق للمراجعة" hint="لا توجد وثائق مطابقة للتصفية." />
        ) : (
          <ListTable headers={['الزبون', 'النوع', 'الاسم', 'الحالة', 'التاريخ', 'إجراءات']}>
            <tbody>
              {filtered.map((d) => (
                <tr key={str(d.id)}>
                  <td>
                    <div className="cell-stack">
                      <strong>{str(d.user_name)}</strong>
                      <small className="muted">{str(d.user_phone)}</small>
                    </div>
                  </td>
                  <td>{DOC_LABELS[str(d.doc_type)] ?? str(d.doc_type ?? d.document_type)}</td>
                  <td>
                    <div className="cell-stack">
                      <span>{str(d.full_name, '—')}</span>
                      {d.national_id ? <small className="muted">{str(d.national_id)}</small> : null}
                    </div>
                  </td>
                  <td>
                    <StatusBadge status={d.status} />
                  </td>
                  <td>{formatDateTime(d.created_at)}</td>
                  <td>
                    <div className="table-actions">
                      <Button type="button" variant="secondary" onClick={() => setPreview(d)}>
                        معاينة
                      </Button>
                      {str(d.status) === 'pending' ? (
                        <>
                          <Button type="button" onClick={() => void review(d.id, 'approve')}>
                            قبول
                          </Button>
                          <Button type="button" variant="danger" onClick={() => void review(d.id, 'reject')}>
                            رفض
                          </Button>
                        </>
                      ) : (
                        <small className="muted">{str(d.admin_note, '—')}</small>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ListTable>
        )}
      </SectionCard>

      <Modal
        open={!!preview}
        wide
        title="معاينة الوثائق"
        subtitle={
          preview
            ? `${str(preview.user_name)} — ${DOC_LABELS[str(preview.doc_type)] ?? str(preview.doc_type)}`
            : undefined
        }
        onClose={() => setPreview(null)}
      >
        {preview ? (
          <div className="compliance-preview stack-gap">
            <div className="detail-grid">
              <div className="kv-row">
                <span className="kv-label">الاسم الكامل</span>
                <span className="kv-value">{str(preview.full_name, '—')}</span>
              </div>
              <div className="kv-row">
                <span className="kv-label">الهاتف</span>
                <span className="kv-value">{str(preview.user_phone, '—')}</span>
              </div>
              <div className="kv-row">
                <span className="kv-label">الرقم الوطني</span>
                <span className="kv-value">{str(preview.national_id, '—')}</span>
              </div>
              <div className="kv-row">
                <span className="kv-label">العنوان</span>
                <span className="kv-value">{previewAddress}</span>
              </div>
              <div className="kv-row">
                <span className="kv-label">نوع الوثيقة</span>
                <span className="kv-value">{DOC_LABELS[str(preview.doc_type)] ?? str(preview.doc_type)}</span>
              </div>
              <div className="kv-row">
                <span className="kv-label">تاريخ الإرسال</span>
                <span className="kv-value">{formatDateTime(preview.created_at)}</span>
              </div>
              <div className="kv-row">
                <span className="kv-label">الحالة</span>
                <span className="kv-value">
                  <StatusBadge status={preview.status} />
                </span>
              </div>
              {preview.admin_note ? (
                <div className="kv-row">
                  <span className="kv-label">ملاحظة الإدارة</span>
                  <span className="kv-value">{str(preview.admin_note)}</span>
                </div>
              ) : null}
            </div>

            {previewFiles.length === 0 ? (
              <Empty title="لا مرفقات" hint="لم يُحفظ أي ملف مع هذا الطلب." />
            ) : (
              <div className="compliance-images">
                {previewFiles.map((file) => (
                  <figure key={`${str(preview.id)}-${file.index}`}>
                    {file.isImage ? (
                      <ComplianceAuthImage apiPath={file.apiPath} label={file.label} />
                    ) : (
                      <div className="compliance-image-fallback">
                        ملف {file.ext.toUpperCase() || 'مرفق'}
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => void openAttachment(file.apiPath)}
                        >
                          فتح المرفق
                        </Button>
                      </div>
                    )}
                    <figcaption>{file.label}</figcaption>
                  </figure>
                ))}
              </div>
            )}

            {str(preview.status) === 'pending' ? (
              <div className="modal-actions">
                <Button type="button" onClick={() => void review(preview.id, 'approve')}>
                  قبول
                </Button>
                <Button type="button" variant="danger" onClick={() => void review(preview.id, 'reject')}>
                  رفض
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </ScreenFrame>
  );
}

async function openAttachment(apiPath: string): Promise<void> {
  try {
    const base = apiBaseUrl(import.meta.env.VITE_API_BASE);
    const url = `${base}${apiPath.startsWith('/') ? apiPath : `/${apiPath}`}`;
    const token = getToken();
    const res = await fetch(url, {
      headers: {
        'X-App-Client': 'admin',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) {
      window.alert('تعذر فتح المرفق.');
      return;
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch {
    window.alert('تعذر فتح المرفق.');
  }
}
